// Ghost pending market orders: the subgraph occasionally misses a market
// order's terminal event, leaving it `isPending` forever while on-chain it was
// executed or cancelled and fully unregistered. Cancelling such an order
// reverts (`NoTradeToTimeoutFound`), so it must not be surfaced. The opposite
// case — an order still registered on-chain past the timeout window — consumes
// the trader's pending-order slots (`MaxPendingMarketOrdersReached`) and MUST
// stay visible so the user cancels it via the market-timeout call.

import type { Address } from 'viem';
import type { RawOrder } from './raw.js';

/**
 * Age past which a subgraph-pending market order cannot still be in flight:
 * on-chain `marketOrdersTimeout` is 50 blocks (~13s on Arbitrum). The margin
 * absorbs clock skew and subgraph indexing lag. Anything older is either
 * cancellable (still registered on-chain) or a ghost (unregistered).
 */
export const STALE_PENDING_MARKET_ORDER_AGE_S = 120;

/**
 * Resolves whether a pending market order is still registered on-chain.
 * `true` = registered (cancellable), `false` = ghost, `undefined` = unknown
 * (RPC failure — callers fail open and keep the order visible).
 */
export type PendingOrderRegisteredReader = (orderId: string) => Promise<boolean | undefined>;

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/** `IOstiumTradingStorage.reqID_pendingMarketOrder` public-mapping getter. */
const REQ_ID_PENDING_MARKET_ORDER_ABI = [
  {
    type: 'function',
    name: 'reqID_pendingMarketOrder',
    stateMutability: 'view',
    inputs: [{ name: '', type: 'uint256' }],
    outputs: [
      { name: 'block', type: 'uint256' },
      { name: 'wantedPrice', type: 'uint192' },
      { name: 'slippageP', type: 'uint32' },
      {
        name: 'trade',
        type: 'tuple',
        components: [
          { name: 'collateral', type: 'uint256' },
          { name: 'openPrice', type: 'uint192' },
          { name: 'tp', type: 'uint192' },
          { name: 'sl', type: 'uint192' },
          { name: 'trader', type: 'address' },
          { name: 'leverage', type: 'uint32' },
          { name: 'pairIndex', type: 'uint16' },
          { name: 'index', type: 'uint8' },
          { name: 'buy', type: 'bool' },
          { name: 'isDayTrade', type: 'bool' },
        ],
      },
      { name: 'percentage', type: 'uint16' },
    ],
  },
] as const;

interface ContractReader {
  readContract(args: {
    address: Address;
    abi: typeof REQ_ID_PENDING_MARKET_ORDER_ABI;
    functionName: 'reqID_pendingMarketOrder';
    args: [bigint];
  }): Promise<unknown>;
}

/**
 * Build a registered-reader backed by `reqID_pendingMarketOrder` on
 * TradingStorage. Ghost verdicts are memoized for the reader's lifetime — an
 * unregistered order can never come back.
 */
export function makePendingOrderRegisteredReader(
  client: ContractReader,
  tradingStorage: Address,
): PendingOrderRegisteredReader {
  const knownGhosts = new Set<string>();

  return async orderId => {
    if (knownGhosts.has(orderId)) return false;
    try {
      const result = (await client.readContract({
        address: tradingStorage,
        abi: REQ_ID_PENDING_MARKET_ORDER_ABI,
        functionName: 'reqID_pendingMarketOrder',
        args: [BigInt(orderId)],
      })) as [bigint, bigint, number, { trader: Address }, number];

      const registered = result[3].trader.toLowerCase() !== ZERO_ADDRESS;
      if (!registered) knownGhosts.add(orderId);
      return registered;
    } catch {
      return undefined;
    }
  };
}

function isStalePendingMarketOrder(order: RawOrder, nowS: number): boolean {
  if (order.orderType !== 'Market') return false;
  if (order.isPending === false || order.isCancelled === true) return false;
  const initiatedAt = parseInt(order.initiatedAt || '0', 10);
  if (!Number.isFinite(initiatedAt) || initiatedAt <= 0) return false;
  return nowS - initiatedAt > STALE_PENDING_MARKET_ORDER_AGE_S;
}

/**
 * Drop pending market orders that no longer exist on-chain. Orders within the
 * in-flight window and non-market orders pass through without any RPC cost;
 * verification failures fail open (the order stays visible).
 */
export async function dropGhostPendingMarketOrders(
  orders: RawOrder[],
  readRegistered: PendingOrderRegisteredReader,
): Promise<RawOrder[]> {
  const nowS = Math.floor(Date.now() / 1000);
  const stale = orders.filter(order => isStalePendingMarketOrder(order, nowS));
  if (stale.length === 0) return orders;

  const ghosts = new Set<string>();
  await Promise.all(stale.map(async order => {
    if ((await readRegistered(order.id)) === false) ghosts.add(order.id);
  }));

  if (ghosts.size === 0) return orders;
  return orders.filter(order => !ghosts.has(order.id));
}
